import { Badge, MessageBar, MessageBarBody, makeStyles, tokens } from '@fluentui/react-components';
import { QueryAggregate, type TableRecord } from '@powerportalspro/core';
import { useT } from '@powerportalspro/react';
import {
  ExportGridButton,
  GridColumn,
  GridColumns,
  GridRelatedRecordsColumn,
  GridToolbarItems,
  MainGrid,
  NavigateNewRecordGridButton,
  NavigateOpenRecordGridButton,
  PagingMode,
} from '@powerportalspro/react-fluent';
import { useMemo } from 'react';
import {
  ALL_ACCOUNTS_VIEW_ID,
  MY_ACCOUNTS_VIEW_ID,
  getAllAccountsView,
  getMyAccountsView,
  useCurrentUserId,
} from './AccountAndContactCustomViews';

// The accounts, each row listing the records related to it — in two <GridRelatedRecordsColumn>s
// over the account's one-to-many relationships:
//
//   - Contacts       — contact_customer_accounts, the contacts whose company is the account:
//                      their names, comma-separated, each a link to the contact's page.
//   - Opportunities  — opportunity_customer_accounts, the opportunities whose customer is the
//                      account, summed up rather than listed: a cellRenderer counts them by
//                      status and shows a badge each for those open, won and lost. `columns` asks
//                      for the status, which the related records are read without otherwise.
//
// Each column loads in one query for the whole page of accounts, not one per row, and the grid's
// search looks in them too: searching for a contact's name finds their account.
//
// The grid scrolls infinitely: the next 20 accounts load as the end of the list comes into view,
// with no pager. Its totals row counts the accounts and totals their revenue, over every account
// the grid matches rather than the ones on screen.

const STRINGS_BASE =
  'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Demos.GridDemos.InlineRelatedDataMainDemo';

const ACCOUNT_URL = 'demos/account';
const CONTACT_URL = 'demos/contact';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalMNudge,
  },
  badges: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalXS,
  },
});

// How many of the opportunities are in each status: 0 open, 1 won, 2 lost.
function countByStatus(records: readonly TableRecord[]): Map<number, number> {
  const counts = new Map<number, number>();
  for (const record of records) {
    const cell = record.properties?.['statecode'];
    const value = cell && typeof cell === 'object' && 'value' in cell ? Number(cell.value ?? 0) : 0;
    counts.set(value, (counts.get(value) ?? 0) + 1);
  }
  return counts;
}

export function InlineRelatedDataMainSample() {
  const styles = useStyles();
  const t = useT();
  const currentUserId = useCurrentUserId();

  const accountViews = useMemo(
    () => [getAllAccountsView(), getMyAccountsView(currentUserId)],
    [currentUserId],
  );

  return (
    <div className={styles.root}>
      <MessageBar intent="warning" layout="multiline">
        <MessageBarBody>{t(`${STRINGS_BASE}.login-required`)}</MessageBarBody>
      </MessageBar>

      <MainGrid
        tableName="account"
        minHeight="550px"
        maxHeight="550px"
        pagingMode={PagingMode.Virtualize}
        pageSize={20}
        viewIds={[ALL_ACCOUNTS_VIEW_ID, MY_ACCOUNTS_VIEW_ID]}
        customViewDefinitions={accountViews}
        defaultViewId={ALL_ACCOUNTS_VIEW_ID}
        persistedStateQueryParameter="accountsGridState"
      >
        <GridColumns>
          {/* Account Name — the built-in cell. */}
          <GridColumn
            columnName="name"
            displayName={t(`${STRINGS_BASE}.col-account-name`)}
            width="220px"
            aggregates={[{ aggregate: QueryAggregate.Count }]}
          />

          <GridColumn
            columnName="revenue"
            displayName={t(`${STRINGS_BASE}.col-revenue`)}
            width="150px"
            aggregates={[{ aggregate: QueryAggregate.Sum, format: 'N0' }]}
          />

          {/* Contacts — the default rendering: names, sorted and comma-separated, each a link to
              the contact's page. */}
          <GridRelatedRecordsColumn
            relationshipName="contact_customer_accounts"
            displayName={t(`${STRINGS_BASE}.col-contacts`)}
            width="340px"
            urlFor={(contact) => `${CONTACT_URL}?contactId=${contact.id}`}
          />

          {/* Opportunities — counted by status: a badge each for those open, won and lost. */}
          <GridRelatedRecordsColumn
            relationshipName="opportunity_customer_accounts"
            displayName={t(`${STRINGS_BASE}.col-opportunities`)}
            width="280px"
            columns={['statecode']}
            cellRenderer={({ records }) => {
              if (records.length === 0) return <span>—</span>;
              const counts = countByStatus(records);
              return (
                <div className={styles.badges}>
                  <Badge appearance="tint" color="brand">
                    {t(`${STRINGS_BASE}.opportunities-open`, [String(counts.get(0) ?? 0)])}
                  </Badge>
                  <Badge appearance="tint" color="success">
                    {t(`${STRINGS_BASE}.opportunities-won`, [String(counts.get(1) ?? 0)])}
                  </Badge>
                  <Badge appearance="tint" color="danger">
                    {t(`${STRINGS_BASE}.opportunities-lost`, [String(counts.get(2) ?? 0)])}
                  </Badge>
                </div>
              );
            }}
          />
        </GridColumns>
        <GridToolbarItems>
          <NavigateNewRecordGridButton url={ACCOUNT_URL} />
          <NavigateOpenRecordGridButton urlFor={(record) => `${ACCOUNT_URL}?accountId=${record.id}`} />
          {/* Exports each related-records column as it shows its records. */}
          <ExportGridButton />
        </GridToolbarItems>
      </MainGrid>
    </div>
  );
}
